import { Maybe } from "@/domains/common/types";
import { withDexieErrorHandling } from "@/domains/dexie-db/dbErrorHandling";
import { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { api } from "@/modules/api";
import { isClientUpgradeError } from "@/store/utils/errors";
import { AppStore } from "@/store/AppStore";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import {
  SyncModelData,
  SyncUpdate,
  SyncUpdateValue,
  SyncModelKind,
  OptimisticSyncUpdate,
  SerializedOptimisticUpdate,
  HydrationSyncUpdateResponse,
} from "@/store/sync/types";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { liveQuery, Table, type Subscription } from "dexie";
import {
  ObservableMap,
  ObservableSet,
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  runInAction,
} from "mobx";
import { computedFn, ILazyObservable, lazyObservable } from "mobx-utils";
import pRetry, { AbortError, FailedAttemptError } from "p-retry";

const BULK_RECOMPUTE_BATCH_SIZE = 70_000;

export abstract class BaseSyncModelStore<
  SyncModel extends BaseSyncModel<ModelData>,
  ModelData extends SyncModelData,
> extends AppSubStore {
  public pool: ObservableMap<Uuid, WeakRef<SyncModel> | undefined> = new ObservableMap();

  public size: number = -1; // -1 means not loaded yet
  public ids: ObservableSet<Uuid> = new ObservableSet();
  public idsLoaded: boolean = false;
  public idsSubscription: Maybe<Subscription>;
  public sizeSubscription: Maybe<Subscription>;
  public modelKind: SyncModelKind;

  private bulkRemoteUpdatesPool: SyncUpdateValue<SyncModelData>[] = [];
  public constructor({
    modelKind,
    ...injectedDeps
  }: { modelKind: SyncModelKind } & AppSubStoreArgs) {
    super(injectedDeps);
    this.modelKind = modelKind;

    makeObservable<
      this,
      | "loadModel"
      | "loadModels"
      | "createLazyObservable"
      | "createLazyObservableMany"
      | "bulkRemoteUpdatesPool"
      | "grantAccessToModelIfItExistsLocally"
      | "revokeAccessToModelIfItExistsLocally"
      | "hydrateModels"
    >(this, {
      computeIndexes: true,
      getAsync: false,
      getManyAsync: false,
      getAllAsync: false,
      createSyncModel: false,

      idsSubscription: observable,
      sizeSubscription: observable,
      idsLoaded: observable,
      ids: observable,
      size: observable,
      all: computed,
      unsubscribeFromIds: action,
      subscribeToIds: action,
      subscribeToSize: action,
      unsubscribeFromSize: action,
      has: false,
      doesNotExist: false,

      createLazyObservable: true,
      createLazyObservableMany: true,
      db: computed,
      modelKind: observable,

      get: false,
      getMany: false,
      pool: observable,
      loadModel: action,
      loadModels: action,

      remoteTable: computed,
      localTable: computed,
      processSyncUpdate: action,
      recompute: action,
      bulkRecompute: action,
      bulkRecomputeAll: action,
      updateObservable: action,

      resetSync: action,
      flushRemote: action,
      bulkRemoteUpdatesPool: false,
      dryProcessSyncUpdate: true,
      grantAccessToModelIfItExistsLocally: false,
      revokeAccessToModelIfItExistsLocally: false,
      hydrateModels: false,
    });

    onBecomeObserved(this, "ids", () => this.subscribeToIds());
    onBecomeUnobserved(this, "ids", () => this.unsubscribeFromIds());
    onBecomeObserved(this, "size", () => this.subscribeToSize());
    onBecomeUnobserved(this, "size", () => this.unsubscribeFromSize());
  }

  public subscribeToIds() {
    this.idsSubscription?.unsubscribe();
    this.idsSubscription = liveQuery(() => this.localTable.toCollection().primaryKeys()).subscribe({
      next: ids =>
        runInAction(() => {
          this.ids.replace(ids);
          this.idsLoaded = true;
        }),
    });
  }

  public unsubscribeFromIds() {
    this.idsSubscription?.unsubscribe();
    runInAction(() => (this.idsLoaded = false));
  }

  public subscribeToSize() {
    this.sizeSubscription?.unsubscribe();
    this.sizeSubscription = liveQuery(() => this.localTable.count()).subscribe({
      next: count =>
        runInAction(() => {
          this.size = count;
        }),
    });
  }

  public unsubscribeFromSize() {
    this.sizeSubscription?.unsubscribe();
  }

  public get db() {
    return this.store.memDb;
  }

  public doesNotExist = (id: string): boolean => {
    if (this.pool.get(id)?.deref()) return false;

    // If ids are not loaded yet, return false because we don't know yet
    // NOTE: this.has(id) needs to be called first to trigger onBecomeObserved
    const hasId = this.has(id);
    if (!this.idsLoaded) return false;
    return !hasId;
  };

  abstract createSyncModel(updateValue: SyncUpdateValue<ModelData>): SyncModel;

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<SyncUpdateValue<ModelData>>;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind].local as Table<SyncUpdateValue<ModelData>>;
  }

  public has = computedFn((id: Uuid): boolean => {
    return this.ids.has(id);
  });

  public get all() {
    // DO NOT USE UNLESS NECESSARY
    const ids = Array.from(this.ids.values());
    return this.getMany(ids);
  }

  public get = computedFn((id: Uuid): SyncModel | undefined => {
    const model = this.pool.get(id)?.deref();
    if (model) return model;
    return this.createLazyObservable(id).current();
  });

  public getMany = computedFn((ids: Uuid[]): SyncModel[] => {
    return this.createLazyObservableMany(ids).current() || [];
  });

  private createLazyObservable(id: Uuid): ILazyObservable<SyncModel | undefined> {
    return lazyObservable((sink: (value: SyncModel | undefined) => void) =>
      this.loadModel(id, sink)
    );
  }

  private createLazyObservableMany(ids: Uuid[]): ILazyObservable<SyncModel[]> {
    return lazyObservable((sink: (value: SyncModel[]) => void) =>
      this.loadModels(ids, sink)
    ) as ILazyObservable<SyncModel[]>;
  }

  public async getAsync(id: Uuid): Promise<SyncModel | undefined> {
    const existing = this.pool.get(id)?.deref();
    if (existing) return existing;

    const data = await this.localTable.get(id);
    if (!data) return undefined;

    const model = this.createSyncModel(data);
    runInAction(() => this.pool.set(id, new WeakRef(model)));
    return model;
  }

  public async getManyAsync(ids: Uuid[]): Promise<SyncModel[]> {
    const models: SyncModel[] = [];
    for (const id of ids) {
      const model = await this.getAsync(id);
      if (model) models.push(model);
    }
    return models;
  }

  public async getAllAsync(): Promise<SyncModel[]> {
    const ids = await this.localTable.orderBy("model_id").primaryKeys();
    return this.getManyAsync(ids);
  }

  private async loadModel(id: Uuid, sink: (value: SyncModel | undefined) => void) {
    if (id === undefined) return;
    const data = await this.localTable.get(id);
    if (!data) {
      sink(undefined);
      return;
    }
    const model = this.createSyncModel(data);
    runInAction(() => this.pool.set(id, new WeakRef(model)));
    sink(model);
  }

  private async loadModels(ids: Uuid[], sink: (value: SyncModel[]) => void) {
    const models: SyncModel[] = [];
    const idsToLoad: Uuid[] = [];

    for (const id of ids) {
      const existingModel = this.pool.get(id)?.deref();
      if (existingModel) models.push(existingModel);
      else idsToLoad.push(id);
    }

    if (idsToLoad.length > 0) {
      const data = await this.localTable.bulkGet(idsToLoad);
      runInAction(() => {
        data.forEach((item, index) => {
          if (item) {
            const model = this.createSyncModel(item);
            const id = idsToLoad[index];
            this.pool.set(id, new WeakRef(model));
            models.push(model);
          }
        });
      });
    }

    sink(models);
  }

  public async processSyncUpdate(
    update: SyncUpdate<ModelData>,
    _options: { hydrating?: boolean; endpoint?: string } = {}
  ) {
    switch (update.kind) {
      case "ACL_UPSERTED":
      case "UPSERTED": {
        const updateValue = update.value as SyncUpdateValue<ModelData>;
        // in case model was re-shared with us
        this.grantAccessToModelIfItExistsLocally(updateValue.model_id);
        await withDexieErrorHandling(
          async () => {
            const remoteData = await this.remoteTable.get(updateValue.model_id);
            if (updateValue.model_version < (remoteData?.model_version ?? 0)) return;
            await this.remoteTable?.put(updateValue, updateValue.model_id);
          },
          this.store,
          `[BaseSyncModelStore] processSyncUpdate`
        );
        break;
      }
      case "DELETED": {
        const modelId = update.value.model_id;
        await this.remoteTable?.delete(modelId);
        break;
      }
      case "ACL_REVOKED": {
        const modelId = update.value.model_id;
        this.revokeAccessToModelIfItExistsLocally(modelId);
        await this.remoteTable?.delete(modelId);
        break;
      }
    }

    await this.recompute(update.value.model_id);
  }

  // TODO: relator to make it reusable with regular processSyncUpdate
  public dryProcessSyncUpdate(update: SyncUpdate<ModelData>): boolean {
    switch (update.kind) {
      case "ACL_UPSERTED":
      case "UPSERTED": {
        // DEXIE REFACTOR TODO: Check model version first
        const value = update.value as SyncUpdateValue<ModelData>;
        this.bulkRemoteUpdatesPool.push(value);
        return true;
      }
      case "DELETED":
      case "ACL_REVOKED": {
        // noop
        return false;
      }
    }

    return false;
  }

  public resetSync() {
    this.pool.clear();
    // TODO: Reset Dexie tables?
  }

  public computeIndexes(_params: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<ModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<ModelData>[];
  }): Record<string, unknown> {
    return {};
  }

  public async recompute(modelId: Uuid) {
    const remoteData = await this.remoteTable.get(modelId);
    const optimisticUpdates = await this.db.queue.optimisticUpdates
      .where({ model_id: modelId })
      .sortBy("locally_committed_at");

    const lastOptimisticUpdate = optimisticUpdates.at(-1);
    if (lastOptimisticUpdate?.kind === "DELETED" || lastOptimisticUpdate?.kind === "ACL_REVOKED") {
      logger.debug({
        message: `[BASE SYNC MODEL STORE] deleting / revoking access`,
        info: { modelId, modelKind: this.modelKind },
      });

      await this.localTable.delete(modelId);
      runInAction(() => this.pool.delete(modelId));
      return;
    }

    const data = lastOptimisticUpdate?.value || remoteData;
    if (!data) {
      // Case: remoteData is deleted and there are no optimistic updates\
      await this.localTable.delete(modelId);
      runInAction(() => this.pool.delete(modelId));
      return;
    }

    const indexes = this.computeIndexes({ store: this.store, remoteData, optimisticUpdates });

    const dataWithIndexes: SyncUpdateValue<ModelData> & Record<string, unknown> = {
      ...(data as SyncUpdateValue<ModelData>),
      ...indexes,
    };

    await withDexieErrorHandling(
      async () => {
        await this.localTable.put(dataWithIndexes, dataWithIndexes.model_id);
      },
      this.store,
      `[BaseSyncModelStore] recompute`
    );

    // this is a noop update to retrigger get for observables that previously returned undefined in the case they they become defined through the recompute
    if (!this.pool.has(modelId)) {
      runInAction(() => this.pool.set(modelId, undefined));
      runInAction(() => this.pool.delete(modelId));
    }
  }

  public async bulkRecomputeAll() {
    const modelIds = await this.remoteTable.orderBy("model_id").primaryKeys();
    const batchSize = Math.min(modelIds.length, BULK_RECOMPUTE_BATCH_SIZE);
    for (let i = 0; i < modelIds.length; i += batchSize) {
      const batchModelIds = modelIds.slice(i, i + batchSize);
      await this.bulkRecompute(batchModelIds);
    }
  }

  public async bulkRecompute(modelIds: Uuid[]) {
    const remoteData = await this.remoteTable.bulkGet(modelIds);

    const allOptimisticUpdates = await this.db.queue.optimisticUpdates
      .where("model_id")
      .anyOf(modelIds)
      .sortBy("locally_committed_at");

    const optimisticUpdatesById = new Map<Uuid, SerializedOptimisticUpdate<SyncModelData>[]>();
    for (const update of allOptimisticUpdates) {
      optimisticUpdatesById.set(update.model_id, [
        ...(optimisticUpdatesById.get(update.model_id) || []),
        update,
      ]);
    }

    const toDelete: Uuid[] = [];
    const toPut: (SyncUpdateValue<ModelData> & Record<string, unknown>)[] = [];

    for (const [i, modelId] of modelIds.entries()) {
      const optimisticUpdates = optimisticUpdatesById.get(modelId) || [];
      const lastOptimisticUpdate = optimisticUpdates.at(-1);

      if (
        lastOptimisticUpdate?.kind === "DELETED" ||
        lastOptimisticUpdate?.kind === "ACL_REVOKED"
      ) {
        logger.debug({
          message: `[BASE SYNC MODEL STORE] deleting / revoking access`,
          info: { modelId, modelKind: this.modelKind },
        });

        toDelete.push(modelId);
        continue;
      }

      const data = lastOptimisticUpdate?.value || remoteData[i];
      if (!data) {
        // Case: remoteData is deleted and there are no optimistic updates\
        toDelete.push(modelId);
        continue;
      }

      const indexes = this.computeIndexes({
        store: this.store,
        remoteData: remoteData[i],
        optimisticUpdates,
      });

      const dataWithIndexes: SyncUpdateValue<ModelData> & Record<string, unknown> = {
        ...(data as SyncUpdateValue<ModelData>),
        ...indexes,
      };

      toPut.push(dataWithIndexes);
    }

    await withDexieErrorHandling(
      () =>
        this.db.transaction("rw", this.localTable, async () => {
          await this.localTable.bulkDelete(toDelete);
          await this.localTable.bulkPut(toPut);
        }),
      this.store,
      `[BaseSyncModelStore] bulkRecompute`
    );

    runInAction(() => {
      for (const modelId of toDelete) {
        this.pool.delete(modelId);
      }
      // this is a noop update to retrigger get for observables that previously returned undefined in the case they they become defined through the recompute

      for (const data of toPut) {
        const modelId = data.model_id;
        if (!this.pool.has(modelId)) {
          this.pool.set(modelId, undefined);
          this.pool.delete(modelId);
        }
      }
    });
  }

  async updateObservable(modelId: Uuid) {
    // Only update if there is an observable in memory
    const existing = this.pool.get(modelId)?.deref();
    if (existing) await Promise.all([existing.updateFromLocal(), existing.updateFromRemote()]);
  }

  public async flushRemote() {
    const values = this.bulkRemoteUpdatesPool as SyncUpdateValue<ModelData>[];
    await withDexieErrorHandling(
      async () => {
        await this.remoteTable.bulkPut(values);
      },
      this.store,
      `[BaseSyncModelStore] flushRemote`
    );
    this.bulkRemoteUpdatesPool = [];
  }

  private grantAccessToModelIfItExistsLocally(modelId: Uuid) {
    const model = this.get(modelId);
    model?.setCanAccess(true);
  }

  private revokeAccessToModelIfItExistsLocally(modelId: Uuid) {
    const model = this.get(modelId);
    model?.setCanAccess(false);
  }

  protected async hydrateModels(
    modelIds: Uuid[],
    options: {
      retries?: number;
      onlyModelKinds?: SyncModelKind[];
      hydrationSessionCursor?: string;
      endpoint?: string;
    } = {}
  ): Promise<{
    success: boolean;
    updates: SyncUpdate<SyncModelData>[];
    nextPageToken?: string;
    hasNextPage?: boolean;
  }> {
    const {
      retries = 3,
      onlyModelKinds = undefined,
      hydrationSessionCursor = undefined,
      endpoint = "/v2/sync/updates/hydrate",
    } = options;

    let hasError = false;
    const updates: SyncUpdate<SyncModelData>[] = [];
    let nextPageToken: string | undefined = undefined;
    let hasNextPage: boolean = false;

    try {
      const response = await pRetry(
        async () => {
          const params: {
            space_id: string;
            sync_model_kind?: SyncModelKind;
            sync_model_ids?: Uuid[];
            only_sync_model_kinds?: SyncModelKind[];
            hydration_session_cursor?: string;
          } = {
            space_id: this.store.spaces.myPersonalSpaceId,
          };

          if (modelIds.length > 0) {
            params.sync_model_kind = this.modelKind;
            params.sync_model_ids = modelIds;
          }

          if (onlyModelKinds) {
            params.only_sync_model_kinds = onlyModelKinds;
          }

          if (hydrationSessionCursor) {
            params.hydration_session_cursor = hydrationSessionCursor;
          }

          const getHydrationSyncUpdates = api.get as (
            url: "/v2/sync/updates/hydrate",
            options: { params: { query: typeof params } }
          ) => Promise<HydrationSyncUpdateResponse>;

          const resp = await getHydrationSyncUpdates(`/v2/sync/updates/hydrate`, {
            params: {
              query: params,
            },
          });

          if (resp.error) {
            if (isClientUpgradeError(resp.error)) {
              this.store.sync.handleClientUpgradeError();
              logger.error({
                message: "[SYNC][BaseSyncModelStore] Client upgrade error",
                info: {
                  modelIds,
                  modelKind: this.modelKind,
                },
              });
              throw new AbortError("Client upgrade error");
            }

            logger.error({
              message: "[SYNC][BaseSyncModelStore] Error fetching models",
              info: {
                modelIds,
                modelKind: this.modelKind,
                error: objectModule.safeErrorAsJson(resp.error),
              },
            });
            throw new Error(JSON.stringify(resp.error));
          }

          return resp;
        },
        {
          retries,
          onFailedAttempt: (error: FailedAttemptError) => {
            logger.info({
              message: `[SYNC][${this.constructor.name}] Retrying fetch`,
              info: {
                modelIds,
                modelKind: this.modelKind,
                attempt: error.attemptNumber,
                retriesLeft: error.retriesLeft,
              },
            });
          },
        }
      );

      const responseUpdates = response.data?.results || [];
      updates.push(...responseUpdates);
      nextPageToken = response.data?.hydration_session_cursor || undefined;
      hasNextPage = response.data?.has_next_page || false;

      // Process all sync updates
      for (const update of updates) {
        await this.processSyncUpdate(update, { endpoint });

        // Store sync update in local DB
        const syncUpdateId = uuidModule.generate();
        await this.store.sync.db.syncUpdates.put(
          {
            ...update,
            sync_id: `deprecated-${syncUpdateId}`,
          },
          syncUpdateId
        );
      }
    } catch (e) {
      logger.error({
        message: `[SYNC][${this.constructor.name}] Error fetching models`,
        info: {
          modelIds,
          modelKind: this.modelKind,
          error: objectModule.safeErrorAsJson(e as Error),
        },
      });
      hasError = true;
    }

    return {
      success: !hasError,
      updates,
      nextPageToken,
      hasNextPage,
    };
  }
}
